// BLOQUE 34 — Dashboard operativo del rol "Jefe de Compras": 4 cuadros
// (Pedidos por fecha objetivo / Pendientes de retiro-entrega / OP pendientes
// de aprobación / Alquileres) con navegación directa (deep-link) al ítem
// clickeado en su pantalla de origen.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ClipboardList, Truck, Banknote, Home, AlertTriangle,
  ChevronRight, CheckCircle2, Clock,
} from "lucide-react";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { STATUS_LABELS, STATUS_COLORS } from "../compras/constants";
import { ItinerarioWidget } from "../compras/ItinerarioWidget";

const OP_TYPE_LABELS = { contado: "Contado", credito: "Crédito", directo: "Directo" };

function EmptyMini({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-gray-400">
      <CheckCircle2 size={22} className="text-emerald-300 mb-2" />
      <p className="text-xs font-medium">{text}</p>
    </div>
  );
}

function CardShell({ title, icon: Icon, count, testId, children, className = "" }) {
  return (
    <div
      className={`bg-white rounded-2xl border border-gray-100 p-5 fade-in-up ${className}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-50 border border-blue-100/80">
            <Icon size={15} className="text-[#2E6DA4]" />
          </div>
          <h3 className="font-bold text-gray-900 text-[15px]">{title}</h3>
        </div>
        {count > 0 && (
          <span className="text-[11px] bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full">{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

export default function JefeComprasDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard/jefe-compras`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toLocaleDateString("es-PY", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const firstName = user?.name?.split(" ")[0] || "Usuario";

  const pedidos = data?.pedidos || [];
  const retiros = data?.retiros || [];
  const ordenesPago = data?.ordenes_pago || [];
  const alquileres = data?.alquileres || [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1440px] mx-auto" data-testid="dashboard-jefe-compras">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 fade-in-up">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-testid="dashboard-greeting">
            Hola, {firstName}
          </h1>
          <p className="text-sm text-gray-400 mt-1 capitalize">{today}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Listado 1 — Pedidos por fecha objetivo (grande, izquierda) */}
        <CardShell title="Pedidos por Fecha Objetivo" icon={ClipboardList} count={pedidos.length} testId="jc-pedidos-card" className="lg:col-span-2">
          {loading ? (
            <div className="space-y-2">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
          ) : pedidos.length === 0 ? (
            <EmptyMini text="Sin pedidos activos" />
          ) : (
            <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1" data-testid="jc-pedidos-list">
              {pedidos.map((p) => (
                <button
                  key={p._id}
                  onClick={() => navigate(`/compras/materiales?tab=pedidos&open=${encodeURIComponent(p.number)}`)}
                  data-testid={`jc-pedido-row-${p._id}`}
                  className="w-full flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-100 hover:border-[#2E6DA4]/30 hover:shadow-sm transition-all text-left"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] font-semibold text-gray-500">{p.number}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[p.status] || "bg-gray-100 text-gray-500"}`}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                    </div>
                    <p className="text-[13px] font-semibold text-gray-800 truncate mt-0.5">
                      {p.cliente_nombre ? `${p.cliente_nombre} · ` : ""}{p.project_name || "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {p.target_date ? (
                      p.vencido ? (
                        <span className="text-[11px] font-bold text-red-600 bg-red-50 border border-red-100 px-2 py-1 rounded-lg flex items-center gap-1">
                          <AlertTriangle size={11} /> Vencido hace {Math.abs(p.dias_restantes)}d
                        </span>
                      ) : (
                        <span className="text-[11px] font-semibold text-gray-500 bg-gray-50 border border-gray-100 px-2 py-1 rounded-lg flex items-center gap-1">
                          <Clock size={11} /> Faltan {p.dias_restantes}d
                        </span>
                      )
                    ) : (
                      <span className="text-[11px] text-gray-400">Sin fecha</span>
                    )}
                    <ChevronRight size={14} className="text-gray-300" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardShell>

        <div className="space-y-5">
          {/* Listado 2 — Pendientes de retiro/entrega */}
          <CardShell title="Retiro / Entrega Pendiente" icon={Truck} count={retiros.length} testId="jc-retiros-card">
            {loading ? (
              <div className="space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
            ) : retiros.length === 0 ? (
              <EmptyMini text="Sin retiros pendientes" />
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="jc-retiros-list">
                {retiros.map((r) => (
                  <button
                    key={r._id}
                    onClick={() => navigate(`/compras/materiales?tab=retiro&open=${encodeURIComponent(r.number)}`)}
                    data-testid={`jc-retiro-row-${r._id}`}
                    className="w-full flex items-center justify-between gap-2 p-3 rounded-xl border border-gray-100 hover:border-[#2E6DA4]/30 hover:shadow-sm transition-all text-left"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-[11px] font-semibold text-gray-500">{r.number}</p>
                      <p className="text-[12px] font-semibold text-gray-800 truncate">{r.project_name || "—"}</p>
                      <p className="text-[11px] text-gray-400 truncate">{r.provider_name || "—"}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-lg flex-shrink-0 ${r.es_faltante ? "bg-red-100 text-red-700 animate-pulse" : r.paso_faltante === "retiro" ? "bg-lime-100 text-lime-700" : "bg-orange-100 text-orange-700"}`}>
                      {r.es_faltante ? "FALTANTE" : r.paso_faltante === "retiro" ? "Pend. Retiro" : "Pend. Entrega"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </CardShell>

          {/* Listado 3 — OPs pendientes de aprobación */}
          <CardShell title="OP Pendientes de Aprobación" icon={Banknote} count={ordenesPago.length} testId="jc-ops-card">
            {loading ? (
              <div className="space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
            ) : ordenesPago.length === 0 ? (
              <EmptyMini text="Sin OP pendientes" />
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="jc-ops-list">
                {ordenesPago.map((op) => (
                  <button
                    key={op._id}
                    onClick={() => navigate(`/tesoreria/pagos?tab=ordenes&open=${encodeURIComponent(op.number)}`)}
                    data-testid={`jc-op-row-${op._id}`}
                    className="w-full flex items-center justify-between gap-2 p-3 rounded-xl border border-gray-100 hover:border-[#2E6DA4]/30 hover:shadow-sm transition-all text-left"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[11px] font-semibold text-gray-500">{op.number}</span>
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-50 text-[#2E6DA4]">{OP_TYPE_LABELS[op.type] || op.type}</span>
                      </div>
                      <p className="text-[12px] font-semibold text-gray-800 truncate">{op.project_name || "—"}</p>
                    </div>
                    <span className="text-[12px] font-bold text-gray-900 flex-shrink-0">{formatPYG(op.total)}</span>
                  </button>
                ))}
              </div>
            )}
          </CardShell>
        </div>
      </div>

      {/* BLOQUE 55 — Itinerario de la semana (retiros/entregas planificados) */}
      <ItinerarioWidget linkTab="retiro" />

      {/* Cuadro 4 — Alquileres */}
      <CardShell title="Alquileres" icon={Home} count={alquileres.length} testId="jc-alquileres-card">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        ) : alquileres.length === 0 ? (
          <EmptyMini text="Sin alquileres registrados" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jc-alquileres-list">
            {alquileres.map((a) => (
              <button
                key={a._id}
                onClick={() => navigate(`/inventario/alquileres?open=${encodeURIComponent(a.number)}`)}
                data-testid={`jc-alquiler-row-${a._id}`}
                className="text-left p-3.5 rounded-xl border border-gray-100 hover:border-[#2E6DA4]/30 hover:shadow-sm transition-all"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-mono text-[11px] font-semibold text-gray-500">{a.number}</span>
                  {a.finalizado ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Finalizado</span>
                  ) : a.vencido ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Vencido</span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Vence en {a.dias_restantes}d</span>
                  )}
                </div>
                <p className="text-[13px] font-semibold text-gray-800 truncate mb-2">{a.project_name || "—"}</p>
                {a.progreso_pct !== null && a.progreso_pct !== undefined && (
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`h-1.5 rounded-full ${a.vencido ? "bg-red-400" : a.progreso_pct >= 80 ? "bg-amber-400" : "bg-emerald-400"}`}
                      style={{ width: `${a.progreso_pct}%` }}
                    />
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-1.5">Límite: {formatDate(a.alquiler_fecha_limite)}</p>
              </button>
            ))}
          </div>
        )}
      </CardShell>
    </div>
  );
}
